/**
 * O conteúdo de fábrica de "Condições do Personagem" — tudo o que acontece COM a ficha.
 *
 * <p><b>A estrutura é do Theo (19/08/2026):</b> três macro-títulos, nesta ordem, e o
 * índice clicável no alto.
 *
 * <ul>
 *   <li><b>Condições Primárias</b> — o ponto de Vigor (HP), o ponto de Concentração (SP),
 *       a Carga, o ponto de Energia, a escala da dormida, o descanso.</li>
 *   <li><b>Condições do Corpo</b> — Envenenado, Fome, Frio, Cansaço. A Fome mora na aba
 *       Season 2+ (decisão do Theo, mesmo dia).</li>
 *   <li><b>Condições de Combate</b> — as duas listas, todas (decisão do Theo,
 *       22/08/2026): as quatro dele (Atordoado, Caído, Paralisado, Furioso), as das
 *       Mecânicas Místikas (Agarrado, Amedrontado, Impedido/Preso) e os seis status do
 *       catálogo `combat_statuses` do motor (Sangrando, Fraqueza, Lento, Apressado,
 *       Vigilante, Regenerando). Envenenado também é status de combate no motor, e
 *       aparece em "Corpo" porque foi onde o Theo o pôs.</li>
 * </ul>
 *
 * <p><b>Toda condição de combate (e o Envenenado) tem a escada de cinco tiers em
 * branco</b> — pedido do Theo: "cada condição vai ter 5 tiers, deixe uma tabela com 5
 * tiers para preencher". O que as fontes e o motor já sabem fica na prosa do quadro,
 * nunca nos tiers: um chute pré-preenchido se lê como decisão tomada.
 *
 * <p><b>Concentração × Energia.</b> Nos épicos e no GDD, SP e Energia são um só recurso
 * (os épicos chamam de "Energia (SP)"; a mesa chama de Concentração). O Theo pediu os
 * dois como condições distintas; cada quadro diz o que a fonte sabe e o que está em
 * aberto, e o alerta da Energia pergunta qual das duas leituras vale.
 *
 * <p>O que o time gravar por cima vence inteiro — este arquivo é só o ponto de partida.
 */
import { iconeDeCondicao } from './iconesDeConteudo';
import type { GrupoDeQuadros, SecaoDeQuadros } from './PaginaDeQuadros';
import { col, montarTabela } from './tabelasEditaveis';
import type { Tabela } from './tabelasEditaveis';

/** Cada seção ganha o ícone da própria condição, pela chave — ver {@code iconesDeConteudo}. */
const comIcones = (secoes: Record<string, SecaoDeQuadros>): Record<string, SecaoDeQuadros> =>
  Object.fromEntries(
    Object.entries(secoes).map(([chave, s]) => [chave, { ...s, icone: iconeDeCondicao(chave) }])
  );

/** Tabela de duas colunas — a regra e o que vale — para as condições que têm desenho. */
const regraEValor = (linhas: string[][]) =>
  montarTabela([col('regra', 'Regra'), col('valor', 'O que vale', true)], linhas);

/**
 * A escada de cinco tiers de uma condição — em branco, para o Theo preencher.
 *
 * <p>Pedido dele (22/08/2026): "cada condição vai ter 5 tiers, deixe uma tabela com 5
 * tiers para preencher". O que já se sabe de cada condição (motor, fontes) fica na
 * PROSA do quadro, nunca pré-preenchido nos tiers — um chute em célula se lê como
 * decisão tomada.
 */
const cincoTiers = () =>
  montarTabela(
    [col('tier', 'Tier'), col('efeito', 'Efeito', true), col('nota', 'Observação', true)],
    [
      ['Tier 1', '', ''],
      ['Tier 2', '', ''],
      ['Tier 3', '', ''],
      ['Tier 4', '', ''],
      ['Tier 5', '', ''],
    ]
  );

// ------------------------------------------------------ condições primárias

const VIGOR = regraEValor([
  ['O que é', 'A integridade do corpo. Ataques e acidentes o reduzem.'],
  ['Teto', 'Vitalidade×5 + Força×1 + Nível + Equipamentos + bônus (fórmula das Mecânicas Místikas — o GDD registra divergência de multiplicador com o v1.0 e manda valer a mais recente).'],
  ['Chega a zero', 'Morte — e a temporada do personagem acaba.'],
  ['Recupera', 'Ao descansar e dormir (Épico 6); poções (heal) e regeneração por turno travam no teto.'],
  ['No código hoje', 'Não existe como campo — só mana* (o SP). A story I3.0.3 cria vigorCurrent/vigorMax e renomeia mana* para energy* na mesma migration.'],
]);

const CONCENTRACAO = regraEValor([
  ['O que é', 'O pool que o combate gasta: manobras, habilidades e magias. A mesa do Mistik Dream o chama de Concentração; os épicos, de Energia (SP).'],
  ['Teto', '5 + Intelecto×3 + Perícia Energia + bônus (Mecânicas Místikas).'],
  ['Consome', 'Cada manobra, habilidade ou magia em combate declara o custo.'],
  ['Recupera', 'restore_mp por item; regeneração por turno é configuração (combat.mp_regen_per_turn, padrão 0).'],
  ['No código hoje', 'Persistido como manaCurrent/manaMax — nome legado; hoje usado só em combate, default 0.'],
]);

const CARGA = montarTabela(
  [col('faixa', 'Faixa'), col('peso', 'Peso total'), col('agi', 'Penalidade de Agilidade')],
  [
    ['Super Leve', 'até X', '0'],
    ['Leve', 'de X a 2X', '−1'],
    ['Média', 'de 2X a 3X', '−2'],
    ['Pesada', 'de 3X a 4X', '−3'],
    ['Máxima', 'de 4X a 5X', '−4'],
    ['Bloqueio', 'acima de 5X', 'item novo recusado; abre o descarte'],
  ]
);

const ENERGIA = regraEValor([
  ['Leitura 1 — o orçamento do dia (GDD §19)', 'Toda ação custa energia, independente do turno; combater não gasta turno, gasta energia por ação. Energia negativa aplica o estado esgotado: nenhuma ação com custo fica disponível — só resta dormir. Recupera principalmente ao dormir (escala da dormida), secundariamente ao descansar e por itens. Os custos por ação ficam no documento de balanceamento.'],
  ['Leitura 2 — os pontos de ação do combate (GDD §28)', 'Ao iniciar o próprio turno, o combatente recebe quatro pontos somados ao saldo que já tinha, até o teto de sete; cada ação de combate custa pontos. Ganho e teto são balanceamento; a estrutura é canônica.'],
  ['Chega a zero', ''],
  ['Recupera', ''],
  ['No código hoje', 'A energia como custo de ação fora do combate não está implementada. Os pontos de ação existem no motor de combate (AP).'],
]);

const ESCALA_DA_DORMIDA = montarTabela(
  [col('onde', 'Onde dorme'), col('custo', 'Custo'), col('energia', 'Energia devolvida')],
  [
    ['Ao relento, no mato', 'Nenhum', '60'],
    ['Na rua', 'Nenhum', '65'],
    ['Em acampamento', 'Equipamento de acampar', '70 + nível da perícia Acampar'],
    ['Em quarto ruim', 'Ouro', '80'],
    ['Em quarto bom', 'Mais ouro', '90'],
  ]
);

const DESCANSO = montarTabela(
  [col('acao', 'Ação'), col('faz', 'O que faz', true), col('custo', 'Custo / risco')],
  [
    ['Descansar', 'Abre o menu de subações abaixo.', '1 turno; dispara chance de encontro — parar não é ficar seguro.'],
    ['Cochilar', 'Devolve Vigor e Energia; alivia 1 nível de Cansaço.', 'Subação do Descansar.'],
    ['Medicar', 'Gasta insumo, devolve mais Vigor que o cochilo e estanca sangramento.', 'Subação do Descansar; consome o insumo.'],
    ['Cozinhar', 'Produz comida — cujo efeito (digestão, buff) entra no Season 2+.', 'Subação do Descansar; ingredientes + Kit de Cozinha.'],
    ['Estudar · Escrever', 'Entram no Season 2+, com a memória e o grimório.', '—'],
    ['Dormir', 'Encerra o dia do personagem e do jogador. A energia de amanhã vem da escala da dormida; limpa o Cansaço. Dormir cedo desperdiça (acorda no 1º turno mesmo assim); virar a madrugada rouba turnos do amanhã.', '4 turnos fixos; sempre pede confirmação.'],
  ]
);

// ------------------------------------------------------- condições do corpo

const ENVENENADO = cincoTiers();

const FRIO = montarTabela(
  [col('flag', 'Flag de clima da cena'), col('condicao', 'Condição'), col('penalidade', 'Penalidade')],
  [
    ['muito_frio', 'Frio', '−1 em todos os testes'],
    ['congelando', 'Congelando', '−2 em todos os testes'],
    ['sem flag de clima', '—', 'nenhuma'],
  ]
);

const CANSACO = montarTabela(
  [col('nivel', 'Nível'), col('penalidade', 'Penalidade'), col('acontece', 'O que acontece', true)],
  [
    ['Cansado 1', '−1 em todos os testes', ''],
    ['Cansado 2', '−2 em todos os testes', ''],
    ['Cansado 3', '−3 em todos os testes', ''],
    ['Cansado 4', '−4 em todos os testes', ''],
    ['Cansado 5', '—', 'O personagem dorme contra a vontade do jogador. Se isso só consome os turnos restantes ou vira o dia está em aberto — ver Conflitos, na página Personagem.'],
  ]
);

const FOME = montarTabela(
  [col('nivel', 'Nível'), col('forca', 'Força'), col('vitalidade', 'Vitalidade'), col('intelecto', 'Intelecto (Vontade)')],
  [
    ['Fome Leve', '−1', '0', '0'],
    ['Fome Média', '−2', '0', '0'],
    ['Fome Forte', '−3', '−1', '0'],
    ['Fome Potente', '−4', '−2', '−1'],
    ['Fome Violenta', '−5', '−3', '−2'],
  ]
);

// ---------------------------------------------------- condições de combate

/*
 * Todas com a mesma escada de cinco tiers em branco (decisão do Theo, 22/08/2026):
 * as quatro que ele nomeou, as da fonte (Agarrado, Amedrontado, Impedido/Preso) e os
 * seis status do motor. O que cada fonte já sabe está na prosa do quadro.
 */
const ATORDOADO = cincoTiers();
const CAIDO = cincoTiers();
const PARALISADO = cincoTiers();
const FURIOSO = cincoTiers();
const AGARRADO = cincoTiers();
const AMEDRONTADO = cincoTiers();
const IMPEDIDO = cincoTiers();
const SANGRANDO = cincoTiers();
const FRAQUEZA = cincoTiers();
const LENTO = cincoTiers();
const APRESSADO = cincoTiers();
const VIGILANTE = cincoTiers();
const REGENERANDO = cincoTiers();

// ---------------------------------------------------------- prosa e avisos

const PROSA: Record<string, Pick<Tabela, 'texto' | 'alerta'>> = {
  vigor: {
    texto:
      'A primeira das três condições primárias do GDD. Os insumos são canônicos — vitalidade manda, força, nível e equipamento contribuem —; o número é balanceamento.',
  },
  concentracao: {
    texto:
      'O Theo separou a Concentração (SP) da Energia em 19/08/2026 — nos épicos e no GDD as duas eram um recurso só. O que está aqui é o que as fontes dizem do pool de combate; o que muda com a separação é dele para fechar.',
  },
  carga: {
    texto:
      'X = 2 + Força×3 + Vitalidade×1, mais o bônus de mochila — decisão do Theo (19/08/2026): vale a fórmula do código; as Mecânicas Místikas e o GDD diziam só Força. Os intervalos fecham por cima: com Força 3 e Vitalidade 3, X = 14 — peso 14 ainda é Super Leve, 15 vira Leve. O peso conta mochila e equipados (senão a penalidade se burla vestindo tudo). A −AGI morde iniciativa, esquiva, fuga e testes de Agilidade, e deve aparecer decomposta ("Iniciativa: AGI 5 − 2 carga = 3"). Loot que não cabe nunca some em silêncio: abre o descarte, e o recusado fica recuperável na cena de pós-combate.',
    alerta:
      'Hoje o código bloqueia em 1×X, binário, sem faixas nem penalidade de AGI — e conta só a mochila no peso. As cinco faixas são implementação nova (épicos 3 e 4).',
  },
  energia: {
    texto:
      'O Theo pediu a Energia como condição primária separada da Concentração (19/08/2026). As fontes conhecem duas coisas com esse nome — o orçamento do dia e os pontos de ação do combate —, e as duas estão na tabela; "chega a zero" e "recupera" ficam em branco até a escolha.',
    alerta:
      'Qual das duas leituras é "o ponto de Energia"? Responder apaga a outra linha. Se for o orçamento do dia, a escala da dormida logo abaixo é a régua de recuperação.',
  },
  'escala-da-dormida': {
    texto:
      'Dormir custa 4 turnos fixos, sempre com confirmação, e define a energia com que o dia seguinte começa. Os valores são balanceamento provisório — o que é canônico é a ordem dos degraus, e que só o acampamento cresce com perícia (Acampar).',
    alerta:
      'Pendência do GDD: casa própria e sede de guilda estavam no topo da escala anterior e não aparecem na nova; e a escala antiga aplicava penalidade nos testes do dia seguinte para relento e acampamento — substituída ou coexistem?',
  },
  descanso: {
    texto:
      'Parar é o que recupera as primárias — e parar não é ficar seguro: Descansar dispara a tabela de encontros como qualquer ação que consome tempo. A ação Comer entra com a comida e a Fome, no Season 2+.',
  },
  envenenado: {
    texto:
      'Condição do corpo por decisão do Theo (19/08/2026), com a escada de cinco tiers para ele escrever. O que já se sabe: no motor existe como status de combate (poison, categoria dot) — 1 de dano por turno por stack, empilha até 5, duração padrão 5 turnos. Vitalidade governa "resistir a veneno" (GDD), e o épico-03 cita Veneno como condição futura no mesmo padrão de níveis da Fome.',
  },
  frio: {
    texto:
      'Canal flat: soma a penalidade ao resultado do teste, não ao atributo. É fonte virtual: a condição é recalculada pela flag de clima da cena atual e não persiste — entrar na cena fria aplica, sair remove na hora. O enum de flags é vocabulário fechado do conteudista, e o clima quente do deserto reusa o mesmo enum (S1+). Diretriz do Beta: congelando só em cena opcional, nunca gateando quest obrigatória; capa e casaco que mitigam são S1+.',
  },
  cansaco: {
    texto:
      'Canal flat, −1 por nível. Avança ao viajar entre áreas carregado e falhar o teste de Vitalidade: +1 nível — deslocar-se dentro da mesma área não gasta turno, não testa e não cansa. Cochilar reduz 1 nível; dormir limpa tudo.',
    alerta:
      'O teste de Viajar ficou para o Season 2+ (19/08/2026) — e ele é o único gatilho do Cansaço. Até ele entrar, o Cansaço fica sem fonte no jogo; decidir se a escada espera junto ou se ganha outro gatilho no Beta.',
  },
  atordoado: {
    texto:
      'Tiers em branco para o Theo. O que já se sabe: o épico-07 propõe −1 na recuperação de pontos de ação no próximo turno (nunca abaixo de 0), duração padrão 1 turno, causado por crítico contundente. No motor o nome stun está reservado (crit_stun_ap, no_status: stun), sem o efeito.',
  },
  caido: {
    texto:
      'Tiers em branco para o Theo. O que já se sabe: causado pela manobra Derrubar; bloqueia as ações de movimento (Avançar, Recuar, Fugir) até Levantar — que custa 2 pontos de ação (épico-07). A fonte lista a condição sem efeito escrito.',
  },
  paralisado: {
    texto:
      'Criação do Theo — não consta nas Mecânicas Místikas nem no GDD. Tiers em branco para ele escrever.',
  },
  furioso: {
    texto:
      'Criação do Theo — não consta nas Mecânicas Místikas nem no GDD. Tiers em branco para ele escrever.',
  },
  agarrado: {
    texto:
      'Da fonte (Mecânicas, "Em desenvolvimento…"). O que já se sabe: o épico-07 propõe que force a distância 0, trave movimento e fuga, e só libere Soltar-se (teste oposto). Tiers em branco para o Theo.',
  },
  amedrontado: {
    texto:
      'Da fonte (Mecânicas, "Em desenvolvimento…"). O que já se sabe: o épico-07 propõe penalidade em testes de ataque, com duração configurável. Tiers em branco para o Theo.',
  },
  impedido: {
    texto:
      'Da fonte (Mecânicas, "Impedido / Preso", "Em desenvolvimento…"). Proposta do épico-07: trava o movimento mas permite atacar — o contrário do Agarrado; causado por Rede/Enredar. Tiers em branco para o Theo.',
  },
  sangrando: {
    texto:
      'Tiers em branco para o Theo. No motor hoje: status bleed (dot), 2 de dano por turno, sem stack, 3 turnos (V20 — balanceamento provisório). Medicar estanca sangramento (GDD); a condição persiste após o combate e não sai durante a luta.',
  },
  fraqueza: {
    texto:
      'Tiers em branco para o Theo. No motor hoje: status weakness (debuff), −2 de Força, 4 turnos (V20 — provisório).',
  },
  lento: {
    texto:
      'Tiers em branco para o Theo. No motor hoje: status slow (debuff), Avançar e Recuar custam +1 ponto de ação, 3 turnos (V20 — provisório).',
  },
  apressado: {
    texto:
      'Tiers em branco para o Theo. No motor hoje: status haste (buff), Avançar e Recuar custam 1 ponto a menos (mínimo 0), 3 turnos (V20 — provisório).',
  },
  vigilante: {
    texto:
      'Tiers em branco para o Theo. No motor hoje: status dodge_guard (buff), +10 em testes de esquiva passiva e ativa, 3 turnos (V20 — provisório).',
  },
  regenerando: {
    texto:
      'Tiers em branco para o Theo. No motor hoje: status regen (hot), recupera 2 de Vigor por turno, 3 turnos (V20 — provisório).',
  },
};

const PROSA_S2: Record<string, Pick<Tabela, 'texto' | 'alerta'>> = {
  fome: {
    texto:
      'Movida para o Season 2+ — decisão do Theo (19/08/2026). Canal por atributo: reduz o próprio atributo, com piso 0. Quando entrar: todo dia o personagem acorda em Fome Leve; um dia inteiro sem comer sobe um nível no dia seguinte, ancorado no dia do personagem, não no reset global. Qualquer refeição zera todos os níveis de uma vez, e ela não passa de Violenta — as válvulas contra a espiral da morte. A penalidade de Intelecto é a "Vontade" da fonte (Vontade é teste regido por Intelecto). A Fome ataca a Força de propósito: é o eixo de carga, dano e viagem, o mais comum nas builds de caçador — a build forte é a que mais precisa do loop de comida.',
  },
};

// ------------------------------------------------------------------ o mapa

const QUADROS: Record<string, Tabela> = {
  vigor: VIGOR,
  concentracao: CONCENTRACAO,
  carga: CARGA,
  energia: ENERGIA,
  'escala-da-dormida': ESCALA_DA_DORMIDA,
  descanso: DESCANSO,
  envenenado: ENVENENADO,
  frio: FRIO,
  cansaco: CANSACO,
  atordoado: ATORDOADO,
  caido: CAIDO,
  paralisado: PARALISADO,
  furioso: FURIOSO,
  agarrado: AGARRADO,
  amedrontado: AMEDRONTADO,
  impedido: IMPEDIDO,
  sangrando: SANGRANDO,
  fraqueza: FRAQUEZA,
  lento: LENTO,
  apressado: APRESSADO,
  vigilante: VIGILANTE,
  regenerando: REGENERANDO,
};

/** A aba Beta/Season 1 — a chave `condicoes-do-personagem`. */
export const TABELAS: Record<string, Tabela> = Object.fromEntries(
  Object.entries(QUADROS).map(([chave, quadro]) => [chave, { ...PROSA[chave], ...quadro }])
);

export const SECOES: Record<string, SecaoDeQuadros> = comIcones({
  vigor: { titulo: 'O ponto de Vigor (HP)', rotuloDoItem: 'regra' },
  concentracao: { titulo: 'O ponto de Concentração (SP)', rotuloDoItem: 'regra' },
  carga: { titulo: 'A Carga', rotuloDoItem: 'faixa', numerada: true },
  energia: { titulo: 'O ponto de Energia', rotuloDoItem: 'regra' },
  'escala-da-dormida': { titulo: 'A escala da dormida', rotuloDoItem: 'degrau', numerada: true },
  descanso: { titulo: 'O descanso', rotuloDoItem: 'ação' },
  envenenado: { titulo: 'Envenenado', rotuloDoItem: 'regra' },
  frio: { titulo: 'Frio', rotuloDoItem: 'faixa' },
  cansaco: { titulo: 'Cansaço', rotuloDoItem: 'nível', numerada: true },
  atordoado: { titulo: 'Atordoado', rotuloDoItem: 'tier' },
  caido: { titulo: 'Caído', rotuloDoItem: 'tier' },
  paralisado: { titulo: 'Paralisado', rotuloDoItem: 'tier' },
  furioso: { titulo: 'Furioso', rotuloDoItem: 'tier' },
  agarrado: { titulo: 'Agarrado', rotuloDoItem: 'tier' },
  amedrontado: { titulo: 'Amedrontado', rotuloDoItem: 'tier' },
  impedido: { titulo: 'Impedido / Preso', rotuloDoItem: 'tier' },
  sangrando: { titulo: 'Sangrando', rotuloDoItem: 'tier' },
  fraqueza: { titulo: 'Fraqueza', rotuloDoItem: 'tier' },
  lento: { titulo: 'Lento', rotuloDoItem: 'tier' },
  apressado: { titulo: 'Apressado', rotuloDoItem: 'tier' },
  vigilante: { titulo: 'Vigilante', rotuloDoItem: 'tier' },
  regenerando: { titulo: 'Regenerando', rotuloDoItem: 'tier' },
});

/** Os três macro-títulos, na ordem do Theo — e a ordem dos quadros dentro de cada um. */
export const GRUPOS: GrupoDeQuadros[] = [
  {
    id: 'primarias',
    titulo: 'Condições Primárias',
    quadros: ['vigor', 'concentracao', 'carga', 'energia', 'escala-da-dormida', 'descanso'],
  },
  { id: 'corpo', titulo: 'Condições do Corpo', quadros: ['envenenado', 'frio', 'cansaco'] },
  {
    id: 'combate',
    titulo: 'Condições de Combate',
    quadros: [
      'atordoado',
      'caido',
      'paralisado',
      'furioso',
      'agarrado',
      'amedrontado',
      'impedido',
      'sangrando',
      'fraqueza',
      'lento',
      'apressado',
      'vigilante',
      'regenerando',
    ],
  },
];

// ------------------------------------------------------------ a aba Season 2+

const QUADROS_S2: Record<string, Tabela> = { fome: FOME };

/** A aba Season 2+ — a chave `condicoes-do-personagem-season2`, gravada à parte. */
export const TABELAS_S2: Record<string, Tabela> = Object.fromEntries(
  Object.entries(QUADROS_S2).map(([chave, quadro]) => [chave, { ...PROSA_S2[chave], ...quadro }])
);

export const SECOES_S2: Record<string, SecaoDeQuadros> = comIcones({
  fome: { titulo: 'Fome', rotuloDoItem: 'nível', numerada: true },
});

export const GRUPOS_S2: GrupoDeQuadros[] = [
  { id: 'corpo', titulo: 'Condições do Corpo', quadros: ['fome'] },
];
